import React from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useCurrentUser } from '../contexts/AuthContext';
import { useEvents, useCreateEvent, useRsvp, useCancelEvent } from '../hooks/useEvents';
import { EventCard } from '../components/calendar/EventCard';
import { Event, RsvpResponse } from '../types';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  MapPin, 
  Clock, 
  Trophy, 
  ChevronLeft, 
  ChevronRight,
  Check,
  X,
  HelpCircle,
  AlertTriangle,
  Lock
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { cn, safeFormat } from '../lib/utils';
import { startOfMonth, endOfMonth, addMonths, subMonths, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isToday } from 'date-fns';
import { usePermissions } from '../hooks/usePermissions';
import { EmptyState } from '../components/ui/EmptyState';

// --- Main Page ---

export const CalendarPage = () => {
  const { node } = useTenant();
  const { can, isAdmin: isGlobalAdmin } = usePermissions();
  const canManageEvents = isGlobalAdmin || can('EVENT_MANAGE') || can('create_event') || can('create_events') || can('manage_events');
  
  const [view, setView] = React.useState<'MONTH' | 'WEEK' | 'LIST'>('LIST');
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isAllDayForm, setIsAllDayForm] = React.useState(false);
  const [isJointForm, setIsJointForm] = React.useState(false);
  const [selectedEvent, setSelectedEvent] = React.useState<Event | null>(null);
  const [currentDate, setCurrentDate] = React.useState(new Date());

  // Fetch range: current month
  const from = startOfMonth(currentDate).toISOString();
  const to = endOfMonth(currentDate).toISOString();

  const { data: events = [], isLoading } = useEvents(node?.id || '', from, to);
  const createMutation = useCreateEvent(node?.id || '');
  const rsvpMutation = useRsvp(node?.id || '');
  const cancelMutation = useCancelEvent(node?.id || '');

  const handleRsvp = (eventId: string, response: RsvpResponse) => {
    rsvpMutation.mutate({ eventId, response }, {
      onSuccess: () => {
        setSelectedEvent(prev => {
          if (!prev || prev.id !== eventId) return prev;
          const prevRsvp = prev.myRsvp || prev.userRsvp;
          const prevYes = prev.rsvpCounts?.YES ?? 0;
          const nextYes = prevRsvp === 'YES' && response !== 'YES'
            ? Math.max(0, prevYes - 1)
            : prevRsvp !== 'YES' && response === 'YES'
            ? prevYes + 1
            : prevYes;
          return {
            ...prev,
            myRsvp: response,
            userRsvp: response,
            rsvpCounts: {
              ...(prev.rsvpCounts || { YES: 0, NO: 0, MAYBE: 0 }),
              YES: nextYes
            }
          };
        });
      }
    });
  };

  const handleCancelEvent = (eventId: string) => {
    if (!window.confirm('Are you sure you want to cancel this event?')) return;
    cancelMutation.mutate(eventId, {
      onSuccess: () => {
        setSelectedEvent(null);
      }
    });
  };

  const handleSelectEvent = (event: Event) => {
    setSelectedEvent(event);
  };

  const eventsByDateMap = React.useMemo(() => {
    const groups: Record<string, Event[]> = {};
    events.forEach(event => {
      const dateKey = safeFormat(event.startTime, 'yyyy-MM-dd');
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(event);
    });
    Object.keys(groups).forEach(dateKey => {
      groups[dateKey].sort((a, b) => a.startTime.localeCompare(b.startTime));
    });
    return groups;
  }, [events]);

  // Group events by date
  const groupedEvents = React.useMemo(() => {
    return Object.keys(eventsByDateMap)
      .sort()
      .map(date => ({
        date,
        events: eventsByDateMap[date]
      }));
  }, [eventsByDateMap]);

  const calendarDays = React.useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    return eachDayOfInterval({
      start: startOfWeek(monthStart),
      end: endOfWeek(monthEnd)
    });
  }, [currentDate]);

  const handleCreateEvent = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const allDay = formData.get('allDay') === 'on';
    const startDateStr = formData.get('startDate') as string;
    const endDateStr = (formData.get('endDate') as string) || startDateStr;
    const startTimeStr = allDay ? '00:00' : ((formData.get('startTime') as string) || '09:00');
    const endTimeStr = allDay ? '23:59' : ((formData.get('endTime') as string) || '10:00');

    const parsedStart = new Date(`${startDateStr}T${startTimeStr}`);
    const parsedEnd = new Date(`${endDateStr}T${endTimeStr}`);

    const data = {
      title: formData.get('title') as string,
      eventType: formData.get('eventType') as string,
      startTime: !isNaN(parsedStart.getTime()) ? parsedStart.toISOString() : `${startDateStr}T${startTimeStr}:00Z`,
      endTime: !isNaN(parsedEnd.getTime()) ? parsedEnd.toISOString() : `${endDateStr}T${endTimeStr}:00Z`,
      location: formData.get('location') as string,
      description: formData.get('description') as string,
      locked: formData.get('locked') === 'on',
      allDay,
      isJoint: formData.get('isJoint') === 'on',
      homeNodeId: formData.get('homeNodeId') as string || undefined,
      awayNodeId: formData.get('awayNodeId') as string || undefined,
    };

    createMutation.mutate(data, {
      onSuccess: () => {
        setIsModalOpen(false);
        setIsAllDayForm(false);
        setIsJointForm(false);
      }
    });
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-white">Calendar</h1>
          <p className="text-xs md:text-sm text-gray-500">Manage and RSVP to organization events.</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-white p-1 rounded-none dark:bg-slate-800">
            <button 
              onClick={() => setView('MONTH')}
              className={cn(
                "px-3 py-1.5 rounded-none text-xs font-semibold transition-all",
                view === 'MONTH' ? "bg-white text-blue-700 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900" : "text-gray-500 hover:text-gray-700"
              )}
            >
              Month
            </button>
            <button 
              onClick={() => setView('LIST')}
              className={cn(
                "px-3 py-1.5 rounded-none text-xs font-semibold transition-all",
                view === 'LIST' ? "bg-white text-blue-700 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900" : "text-gray-500 hover:text-gray-700"
              )}
            >
              List
            </button>
          </div>

          {canManageEvents && (
            <Button variant="primary" onClick={() => setIsModalOpen(true)}>
              <Plus size={18} />
              New event
            </Button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between bg-white border-2 border-black dark:border-white rounded-none p-4 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {safeFormat(currentDate, 'MMMM yyyy')}
        </h2>
        <div className="flex items-center gap-2">
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={() => setCurrentDate(subMonths(currentDate, 1))}
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </Button>
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={() => setCurrentDate(new Date())}
          >
            Today
          </Button>
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={() => setCurrentDate(addMonths(currentDate, 1))}
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      {/* Month or List View */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-gray-500">Loading events...</p>
        </div>
      ) : view === 'MONTH' ? (
        <div className="bg-white dark:bg-slate-900 border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] overflow-hidden">
          <div className="grid grid-cols-7 border-b-2 border-black dark:border-white bg-gray-50 dark:bg-slate-950">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName) => (
              <div key={dayName} className="py-2.5 text-center text-[10px] font-bold uppercase tracking-widest font-mono text-gray-500 dark:text-gray-400">
                {dayName}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 divide-x divide-y divide-gray-200 dark:divide-slate-800">
            {calendarDays.map((day) => {
              const dateKey = safeFormat(day, 'yyyy-MM-dd');
              const dayEvents = eventsByDateMap[dateKey] || [];
              const inCurrentMonth = isSameMonth(day, currentDate);
              const todayHighlight = isToday(day);
              return (
                <div
                  key={dateKey}
                  className={cn(
                    "min-h-[110px] p-2 transition-colors flex flex-col gap-1.5",
                    !inCurrentMonth && "bg-gray-50/70 text-gray-400 dark:bg-slate-950/60 dark:text-slate-600",
                    todayHighlight && "bg-blue-50/40 dark:bg-blue-950/20"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={cn(
                        "text-xs font-mono font-bold w-6 h-6 flex items-center justify-center",
                        todayHighlight
                          ? "bg-[#0044FF] text-white"
                          : inCurrentMonth
                          ? "text-gray-900 dark:text-gray-100"
                          : "text-gray-400 dark:text-slate-600"
                      )}
                    >
                      {safeFormat(day, 'd')}
                    </span>
                    {dayEvents.length > 0 && (
                      <span className="text-[9px] font-mono font-bold text-gray-400">
                        {dayEvents.length}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1 overflow-y-auto max-h-24">
                    {dayEvents.map((ev) => (
                      <button
                        key={ev.id}
                        type="button"
                        onClick={() => handleSelectEvent(ev)}
                        className={cn(
                          "w-full text-left px-1.5 py-1 text-[10px] font-bold truncate border border-black/20 dark:border-white/20 transition-colors cursor-pointer",
                          ev.status === 'CANCELLED'
                            ? "bg-gray-100 text-gray-400 line-through dark:bg-slate-800"
                            : "bg-blue-50 text-blue-900 hover:bg-[#0044FF] hover:text-white dark:bg-blue-950/50 dark:text-blue-200 dark:hover:bg-[#0044FF]"
                        )}
                        title={ev.title}
                      >
                        {!ev.allDay && <span className="font-mono mr-1 opacity-75">{safeFormat(ev.startTime, 'h:mma')}</span>}
                        {ev.title}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : groupedEvents.length > 0 ? (
        <div className="space-y-8">
          {groupedEvents.map(group => (
            <div key={group.date} className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest font-mono">
                  {safeFormat(group.date, 'EEEE, MMMM do')}
                </span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
              <div className="space-y-4">
                {group.events.map(event => (
                  <EventCard key={event.id} event={event} onClick={handleSelectEvent} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={CalendarIcon}
          title="No Events Scheduled"
          description="There are no events for the selected period. Schedule your first event to start building your organization's calendar."
          actions={
            canManageEvents
              ? [{ label: 'Schedule an Event', onClick: () => setIsModalOpen(true) }]
              : []
          }
          tips={[
            'Events support RSVP tracking so you always know who\'s coming',
            'Use "Joint Events" to schedule cross-organization activities',
            'Navigate months with the arrows above to see past and future events',
          ]}
        />
      )}

      {/* Create Event Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Create New Event"
        className="max-w-2xl"
      >
        <form onSubmit={handleCreateEvent} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input label="Event Title" name="title" required placeholder="e.g. Annual General Meeting" />
            </div>
            
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Event Type</label>
              <select 
                name="eventType" 
                className="w-full px-3 py-2 text-sm border-2 border-black dark:border-white rounded-none bg-white focus:outline-none focus:border-blue-500 dark:bg-slate-900"
              >
                <option value="MEETING">Meeting</option>
                <option value="MATCHUP">Matchup / Game</option>
                <option value="SOCIAL">Social</option>
                <option value="TRAINING">Training</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <div>
              <Input label="Location" name="location" placeholder="e.g. Main Hall or Zoom Link" />
            </div>

            <div>
              <Input label="Start Date" name="startDate" type="date" required defaultValue={safeFormat(new Date(), 'yyyy-MM-dd')} />
            </div>
            {!isAllDayForm && (
              <div>
                <Input label="Start Time" name="startTime" type="time" required defaultValue="09:00" />
              </div>
            )}

            <div>
              <Input label="End Date" name="endDate" type="date" required defaultValue={safeFormat(new Date(), 'yyyy-MM-dd')} />
            </div>
            {!isAllDayForm && (
              <div>
                <Input label="End Time" name="endTime" type="time" required defaultValue="10:00" />
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
              <textarea 
                name="description"
                rows={3}
                className="w-full px-3 py-2 text-sm border-2 border-black dark:border-white rounded-none bg-white focus:outline-none focus:border-blue-500 dark:bg-slate-900"
                placeholder="Details about the event..."
              />
            </div>

            <div className="flex flex-wrap items-center gap-6 md:col-span-2 py-2 border-y border-black dark:border-white">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="allDay"
                  checked={isAllDayForm}
                  onChange={(e) => setIsAllDayForm(e.target.checked)}
                  className="rounded border-black dark:border-white text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">All Day Event</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="locked" className="rounded border-black dark:border-white text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">Lock RSVPs</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="isJoint"
                  checked={isJointForm}
                  onChange={(e) => setIsJointForm(e.target.checked)}
                  className="rounded border-black dark:border-white text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">Joint Event</span>
              </label>
            </div>

            {isJointForm && (
              <>
                <div>
                  <Input label="Home Organization ID (Optional)" name="homeNodeId" defaultValue={node?.id || ''} placeholder="Home Node ID" />
                </div>
                <div>
                  <Input label="Away Organization ID" name="awayNodeId" placeholder="Partner / Away Node ID" />
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" type="button" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button variant="primary" type="submit" isLoading={createMutation.isPending}>
              Create Event
            </Button>
          </div>
        </form>
      </Modal>

      {/* Event Detail Modal */}
      <Modal
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        title="Event Details"
        className="max-w-lg"
      >
        {selectedEvent && (
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-16 h-20 rounded-none bg-navy-900 border-2 border-black dark:border-white border-navy-900 flex flex-col items-center justify-center font-mono">
                <span className="text-xs uppercase font-black text-blue-400 tracking-widest">
                  {safeFormat(selectedEvent.startTime, 'MMM')}
                </span>
                <span className="text-3xl font-black text-white leading-none">
                  {safeFormat(selectedEvent.startTime, 'd')}
                </span>
              </div>
              <div className="flex-1 pt-1">
                <div className="flex flex-wrap gap-2 mb-2">
                  <Badge variant={selectedEvent.eventType === 'MEETING' ? 'blue' : 'gray'} className="text-[10px] font-black uppercase tracking-widest px-2">{selectedEvent.eventType}</Badge>
                  {selectedEvent.isJoint && <Badge variant="purple" className="text-[10px] font-black uppercase tracking-widest px-2">Joint Event</Badge>}
                  {selectedEvent.status === 'CANCELLED' && <Badge variant="red" className="text-[10px] font-black uppercase tracking-widest px-2">Cancelled</Badge>}
                </div>
                <h2 className="text-2xl font-black font-display text-navy-900 dark:text-white leading-tight">
                  {selectedEvent.title}
                </h2>
                {selectedEvent.isJoint && selectedEvent.homeNodeName && selectedEvent.awayNodeName && (
                  <p className="text-sm font-bold text-blue-700 mt-1">
                    {selectedEvent.homeNodeName} <span className="text-gray-400 font-normal mx-1">v</span> {selectedEvent.awayNodeName}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 py-4 border-y border-black dark:border-white">
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">When</p>
                <div className="flex items-center gap-2 text-sm font-medium text-navy-900 dark:text-white">
                  <Clock size={16} className="text-brand-primary" />
                  <span>
                    {selectedEvent.allDay ? 'All Day' : `${safeFormat(selectedEvent.startTime, 'h:mm a')} - ${safeFormat(selectedEvent.endTime, 'h:mm a')}`}
                  </span>
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Where</p>
                <div className="flex items-center gap-2 text-sm font-medium text-navy-900 dark:text-white">
                  <MapPin size={16} className="text-brand-primary" />
                  <span className="truncate">{selectedEvent.location || 'No location set'}</span>
                </div>
              </div>
            </div>

            {selectedEvent.description && (
              <div className="space-y-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">About the event</p>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed bg-white p-4 rounded-none border-2 border-black dark:border-white dark:bg-slate-950">
                  {selectedEvent.description}
                </p>
              </div>
            )}

            {!selectedEvent.locked && selectedEvent.status !== 'CANCELLED' && (
              <div className="flex items-center gap-2 pt-2">
                <Button
                  size="sm"
                  variant={(selectedEvent.myRsvp || selectedEvent.userRsvp) === 'YES' ? 'primary' : 'secondary'}
                  onClick={() => handleRsvp(selectedEvent.id, 'YES')}
                  isLoading={rsvpMutation.isPending}
                >
                  <Check size={14} className="mr-1" /> Going
                </Button>
                <Button
                  size="sm"
                  variant={(selectedEvent.myRsvp || selectedEvent.userRsvp) === 'MAYBE' ? 'primary' : 'secondary'}
                  onClick={() => handleRsvp(selectedEvent.id, 'MAYBE')}
                  isLoading={rsvpMutation.isPending}
                >
                  <HelpCircle size={14} className="mr-1" /> Maybe
                </Button>
                <Button
                  size="sm"
                  variant={(selectedEvent.myRsvp || selectedEvent.userRsvp) === 'NO' ? 'primary' : 'secondary'}
                  onClick={() => handleRsvp(selectedEvent.id, 'NO')}
                  isLoading={rsvpMutation.isPending}
                >
                  <X size={14} className="mr-1" /> Decline
                </Button>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {(selectedEvent.rsvpCounts?.YES ?? 0) > 0 ? (
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-none border-2 border-white bg-brand-primary/10 flex items-center justify-center text-[10px] font-bold text-brand-primary">
                      {selectedEvent.rsvpCounts.YES}
                    </div>
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{selectedEvent.rsvpCounts.YES} {selectedEvent.rsvpCounts.YES === 1 ? 'person' : 'people'} joined</span>
                  </div>
                ) : (
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest pl-1 border-l-2 border-black dark:border-white">No one has joined yet</span>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                {canManageEvents && selectedEvent.status !== 'CANCELLED' && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleCancelEvent(selectedEvent.id)}
                    isLoading={cancelMutation.isPending}
                  >
                    Cancel Event
                  </Button>
                )}
                <Button onClick={() => setSelectedEvent(null)} variant="secondary">Close</Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
